import { useEffect, useId, useMemo, useState } from 'react';
import { ModalDialog } from '../../job-roles/components/ModalDialog';
import { AddContractorDialog } from '../../people/components/AddContractorDialog';
import { peopleGateway } from '../../people';
import { ContractorPayMethod, type ContractorResponse } from '../../people/types';
import { describeContractorFailure } from '../errors';
import type { AddLineInput, AmountMode, ContractorBatchLine, UpdateLineInput } from '../types';

export type PaymentDialogMode = { kind: 'add' } | { kind: 'edit'; line: ContractorBatchLine };

interface Props {
  mode: PaymentDialogMode;
  /** Contractors that already have an included line in this batch (one payment per contractor per batch). */
  takenContractorIds: ReadonlySet<string>;
  onClose: () => void;
  /** Sends the payment to the server; resolves to a plain-language refusal, or null when it was saved (the page then closes this dialog). */
  onSubmit: (input: AddLineInput | UpdateLineInput, lineId: string | null) => Promise<string | null>;
}

type Load = { kind: 'loading' } | { kind: 'error'; message: string } | { kind: 'loaded'; contractors: ContractorResponse[] };

const MONEY = /^\d{1,13}(\.\d{1,2})?$/;
const FOUR_DP = /^\d{1,9}(\.\d{1,4})?$/;

function toContractorUpdate(c: ContractorResponse) {
  return {
    displayName: c.displayName, businessName: c.businessName, email: c.email, identificationType: c.identificationType,
    identificationLast4: c.identificationLast4, preferredPayMethod: ContractorPayMethod.Check,
    // The W-9 / 1099 values are always sent back with the whole record (the server leaves both unchanged only when the 1099 flag is omitted).
    w9ReceivedOn: c.w9ReceivedOn, form1099Configured: c.form1099Configured, isActive: c.isActive,
  };
}

/**
 * Add (or edit) one contractor payment. Dependencies are created in place, over this page: a contractor that does not exist yet is added with the same
 * form and API as the Contractors page, and a contractor with no pay method can be set to Check without leaving. The server stays the authority on
 * eligibility, amounts (hours x rate is computed there; this form never sends a computed amount) and permission.
 */
export function AddPaymentDialog({ mode, takenContractorIds, onClose, onSubmit }: Props) {
  const uid = useId();
  const edit = mode.kind === 'edit' ? mode.line : null;
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [contractorId, setContractorId] = useState(edit?.contractorId ?? '');
  const [description, setDescription] = useState(edit?.description ?? '');
  const [amountMode, setAmountMode] = useState<AmountMode>(edit?.amountMode ?? 'Flat');
  const [amount, setAmount] = useState(edit && edit.amountMode === 'Flat' ? String(edit.amount) : '');
  const [hours, setHours] = useState(edit?.hours != null ? String(edit.hours) : '');
  const [rate, setRate] = useState(edit?.rate != null ? edit.rate.toFixed(4) : '');
  const [errors, setErrors] = useState<Partial<Record<'contractor' | 'description' | 'amount' | 'hours' | 'rate', string>>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  useEffect(() => {
    if (edit) return;
    let cancelled = false;
    peopleGateway
      .listContractors({ isActive: true, page: 1, pageSize: 100 })
      .then((page) => {
        if (!cancelled) setLoad({ kind: 'loaded', contractors: page.items });
      })
      .catch((err: unknown) => {
        if (!cancelled) setLoad({ kind: 'error', message: describeContractorFailure(err, 'The contractors couldn’t be loaded. Try again.').message });
      });
    return () => {
      cancelled = true;
    };
  }, [edit]);

  const contractors = useMemo(() => (load.kind === 'loaded' ? load.contractors : []), [load]);
  const eligible = contractors.filter((c) => c.preferredPayMethod === ContractorPayMethod.Check && !takenContractorIds.has(c.id));
  const unspecified = contractors.filter((c) => c.preferredPayMethod === null && !takenContractorIds.has(c.id));
  const directDeposit = contractors.filter((c) => c.preferredPayMethod === ContractorPayMethod.DirectDeposit).length;
  const selected = contractors.find((c) => c.id === contractorId) ?? null;
  const needsCheckMethod = selected !== null && selected.preferredPayMethod === null;

  async function setToCheck() {
    if (!selected) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await peopleGateway.updateContractor(selected.id, toContractorUpdate(selected));
      setLoad((l) => (l.kind === 'loaded' ? { kind: 'loaded', contractors: l.contractors.map((c) => (c.id === updated.id ? updated : c)) } : l));
      setNote(`${updated.displayName} is now set to be paid by check.`);
    } catch (err) {
      setError(describeContractorFailure(err, 'The contractor’s pay method couldn’t be changed. Try again.').message);
    } finally {
      setBusy(false);
    }
  }

  async function submit() {
    const found: typeof errors = {};
    if (!edit && !selected) found.contractor = 'Choose a contractor.';
    if (!edit && needsCheckMethod) found.contractor = 'Set this contractor to be paid by check first.';
    if (description.trim() === '') found.description = 'Enter a description.';
    else if (description.trim().length > 200) found.description = 'The description must be 200 characters or fewer.';
    let input: UpdateLineInput;
    if (amountMode === 'Flat') {
      if (!MONEY.test(amount.trim()) || Number(amount) <= 0) found.amount = 'Enter an amount greater than zero, with at most two decimals.';
      input = { description: description.trim(), amountMode: 'Flat', amount: Number(amount), hours: null, rate: null };
    } else {
      if (!FOUR_DP.test(hours.trim()) || Number(hours) <= 0) found.hours = 'Enter hours greater than zero (up to four decimals).';
      if (!FOUR_DP.test(rate.trim()) || Number(rate) <= 0) found.rate = 'Enter a rate greater than zero (up to four decimals).';
      // Only hours and rate go to the server; it computes the amount.
      input = { description: description.trim(), amountMode: 'HoursRate', amount: null, hours: Number(hours), rate: Number(rate) };
    }
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    setBusy(true);
    setError(null);
    const refusal = await onSubmit(edit ? input : { contractorId, ...input }, edit?.id ?? null);
    if (refusal) {
      setError(refusal);
      setBusy(false);
    }
  }

  return (
    <>
      <ModalDialog
        title={edit ? `Edit payment — ${edit.contractorName}` : 'Add contractor payment'}
        size="md"
        busy={busy}
        onClose={onClose}
        footer={(
          <>
            <button type="button" className="jr-btn jr-btn--secondary" onClick={onClose} disabled={busy}>Cancel</button>
            <button type="button" className="jr-btn jr-btn--primary" onClick={() => void submit()} disabled={busy || (!edit && load.kind !== 'loaded')}>
              {busy ? 'Saving…' : edit ? 'Save payment' : 'Add payment'}
            </button>
          </>
        )}
      >
        {!edit && (
          <div className="jr-field">
            <label htmlFor={`${uid}-contractor`}>Contractor <span aria-hidden="true" className="jr-req">*</span></label>
            {load.kind === 'loading' && <p className="muted" role="status">Loading contractors…</p>}
            {load.kind === 'error' && <p className="jr-error" role="alert">{load.message}</p>}
            {load.kind === 'loaded' && (
              <>
                <select
                  id={`${uid}-contractor`}
                  data-autofocus
                  value={contractorId}
                  aria-required="true"
                  aria-invalid={errors.contractor ? true : undefined}
                  onChange={(e) => { setContractorId(e.target.value); setNote(null); setError(null); }}
                >
                  <option value="">Select a contractor</option>
                  {eligible.length > 0 && (
                    <optgroup label="Paid by check">
                      {eligible.map((c) => <option key={c.id} value={c.id}>{c.displayName}{c.businessName ? ` — ${c.businessName}` : ''}</option>)}
                    </optgroup>
                  )}
                  {unspecified.length > 0 && (
                    <optgroup label="Pay method not set">
                      {unspecified.map((c) => <option key={c.id} value={c.id}>{c.displayName} (pay method not set)</option>)}
                    </optgroup>
                  )}
                </select>
                {eligible.length === 0 && unspecified.length === 0 && (
                  <p className="muted" data-testid="no-eligible-contractors">No active contractor is available to pay by check. Add a contractor below.</p>
                )}
                {directDeposit > 0 && (
                  <p className="muted">{directDeposit} {directDeposit === 1 ? 'contractor is' : 'contractors are'} paid by direct deposit and not listed here. Paying by direct deposit is coming later.</p>
                )}
                <p>
                  <button type="button" className="link" onClick={() => setAdding(true)} disabled={busy}>Add contractor</button>
                  {' '}<span className="muted">if the contractor isn’t listed.</span>
                </p>
              </>
            )}
            {errors.contractor && <p className="jr-error" role="alert">{errors.contractor}</p>}
            {needsCheckMethod && selected && (
              <div className="wc-advisory" role="note" data-testid="set-check-prompt">
                <span>{selected.displayName} has no pay method set, so they can’t be paid by check yet.</span>
                <button type="button" className="wc-btn wc-btn--mini" onClick={() => void setToCheck()} disabled={busy}>Set pay method to Check</button>
              </div>
            )}
            {note && <p className="success" role="status">{note}</p>}
          </div>
        )}
        <div className="jr-field">
          <label htmlFor={`${uid}-description`}>Description <span aria-hidden="true" className="jr-req">*</span></label>
          <input id={`${uid}-description`} data-autofocus={edit ? true : undefined} value={description} maxLength={200} aria-required="true" aria-invalid={errors.description ? true : undefined} placeholder="For example: October design work" onChange={(e) => setDescription(e.target.value)} />
          {errors.description && <p className="jr-error" role="alert">{errors.description}</p>}
        </div>
        <fieldset className="jr-field" style={{ border: 0, padding: 0, margin: 0 }}>
          <legend className="muted">How is the payment calculated?</legend>
          <label><input type="radio" name={`${uid}-mode`} checked={amountMode === 'Flat'} onChange={() => setAmountMode('Flat')} /> Flat amount</label>
          <label><input type="radio" name={`${uid}-mode`} checked={amountMode === 'HoursRate'} onChange={() => setAmountMode('HoursRate')} /> Hours × rate</label>
        </fieldset>
        {amountMode === 'Flat' ? (
          <div className="jr-field">
            <label htmlFor={`${uid}-amount`}>Amount <span aria-hidden="true" className="jr-req">*</span></label>
            <input id={`${uid}-amount`} inputMode="decimal" value={amount} placeholder="0.00" aria-required="true" aria-invalid={errors.amount ? true : undefined} onChange={(e) => setAmount(e.target.value)} />
            {errors.amount && <p className="jr-error" role="alert">{errors.amount}</p>}
          </div>
        ) : (
          <>
            <div className="jr-field">
              <label htmlFor={`${uid}-hours`}>Hours <span aria-hidden="true" className="jr-req">*</span></label>
              <input id={`${uid}-hours`} inputMode="decimal" value={hours} aria-required="true" aria-invalid={errors.hours ? true : undefined} onChange={(e) => setHours(e.target.value)} />
              {errors.hours && <p className="jr-error" role="alert">{errors.hours}</p>}
            </div>
            <div className="jr-field">
              <label htmlFor={`${uid}-rate`}>Rate per hour <span aria-hidden="true" className="jr-req">*</span></label>
              <input id={`${uid}-rate`} inputMode="decimal" value={rate} placeholder="0.0000" aria-required="true" aria-invalid={errors.rate ? true : undefined} onChange={(e) => setRate(e.target.value)} />
              {errors.rate && <p className="jr-error" role="alert">{errors.rate}</p>}
            </div>
            <p className="muted">The payment amount is calculated when you save the payment and rounded to the cent. No withholding is applied to a contractor payment.</p>
          </>
        )}
        {error && <p className="jr-error" role="alert" data-testid="payment-dialog-error">{error}</p>}
      </ModalDialog>
      {adding && (
        <AddContractorDialog
          onClose={() => setAdding(false)}
          onCreated={(created) => {
            setLoad((l) => (l.kind === 'loaded' ? { kind: 'loaded', contractors: [...l.contractors, created] } : l));
            setContractorId(created.id);
            setNote(`${created.displayName} was added and selected.`);
            setAdding(false);
            // The nested dialog returns focus to the control that opened it (the Add contractor button).
          }}
        />
      )}
    </>
  );
}
